// Gemeinsame Ausstattung aller drei Shells: Avatar-Menü (Konten, Plattformen, Registry-Einträge),
// Befehlspalette (Registry `commands`/`commandProviders` + Seiten aus der Navigation),
// Kürzel-Dialog und Toaster.
import {
  type AccountMenuProps,
  CommandPalette,
  type PaletteCommand,
  type PaletteProvider,
  useCommandPaletteShortcut,
} from '@bar/ui/basis';
import { toast, Toaster } from '@bar/ui/steuerung';
import { ArrowSquareOut, Keyboard, Sparkle } from '@phosphor-icons/react';
import { useAccountMenuItems, useCommandProviders, useCommands } from '@web/registry';
import type { NavItem, PlatformKey } from '@web/registry/types';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { buildAccountEntries } from './account-entries';
import { useBrowserAccounts, useSessionActions } from './data';
import { roleChips, type ShellIdentity } from './identity';
import { commandTarget } from './nav';
import { PLATFORM_PREFIX } from './notifications';
import { ShortcutsDialog } from './ShortcutsDialog';

export interface ShellChrome {
  account: AccountMenuProps;
  openPalette: () => void;
  /** Palette, Kürzel-Dialog und Toaster (in `overlays` der Shell). */
  overlays: ReactNode;
}

export function useShellChrome(
  platform: PlatformKey,
  identity: ShellIdentity,
  pages: readonly NavItem[],
): ShellChrome {
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  useCommandPaletteShortcut(openPalette);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const typing =
        event.target instanceof HTMLElement &&
        (event.target.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName));
      if (event.key === '?' && !typing && !event.metaKey && !event.ctrlKey) setShortcutsOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Das Recht auf Bar Admin entscheidet der Server (`/me.platforms`), nicht die Rollenliste.
  const adminCapable = identity.platforms.admin;
  const contributed = useAccountMenuItems();
  const accounts = useBrowserAccounts(identity);
  const session = useSessionActions();
  const registryCommands = useCommands();
  const registryProviders = useCommandProviders();

  const commands = useMemo<PaletteCommand[]>(() => {
    const fromPages = pages
      .filter((p) => !p.path.includes(':'))
      .map((p) => ({
        id: `page:${p.key}`,
        group: 'Pages',
        label: p.label,
        icon: p.icon,
        href: p.path,
        reload: p.external,
      }));
    const actions: PaletteCommand[] = [
      {
        id: 'ask-ai',
        group: 'Actions',
        label: 'Ask Bar AI',
        icon: Sparkle,
        ...commandTarget('/ai/new', PLATFORM_PREFIX[platform]),
      },
    ];
    if (adminCapable && platform !== 'admin')
      actions.push({
        id: 'to-admin',
        group: 'Actions',
        label: 'Switch to Bar Admin',
        icon: ArrowSquareOut,
        href: '/admin/',
        reload: true,
      });
    if (platform !== 'people')
      actions.push({
        id: 'to-people',
        group: 'Actions',
        label: 'Switch to People app',
        icon: ArrowSquareOut,
        href: '/',
        reload: true,
      });
    const help: PaletteCommand[] = [
      {
        id: 'shortcuts',
        group: 'Help',
        label: 'Keyboard shortcuts',
        icon: Keyboard,
        shortcut: '?',
        perform: () => setShortcutsOpen(true),
      },
    ];
    const fromRegistry = registryCommands.map((c) => {
      const target = c.href ? commandTarget(c.href, PLATFORM_PREFIX[platform]) : undefined;
      return {
        id: c.id,
        group: c.group,
        label: c.label,
        icon: c.icon,
        keywords: c.keywords,
        shortcut: c.shortcut,
        href: target?.href,
        reload: target?.reload,
        perform: c.perform,
      };
    });
    return [...fromRegistry, ...actions, ...fromPages, ...help];
  }, [pages, registryCommands, adminCapable, platform]);

  const providers = useMemo<PaletteProvider[]>(
    () =>
      registryProviders.map((p) => ({
        id: p.id,
        group: p.group,
        order: p.order,
        search: async (query: string, signal: AbortSignal) =>
          (await p.search(query, signal)).map((r) => ({
            ...r,
            ...commandTarget(r.href, PLATFORM_PREFIX[platform]),
          })),
      })),
    [registryProviders, platform],
  );

  const account: AccountMenuProps = {
    person: {
      id: identity.employeeId ?? identity.accountId,
      name: identity.name,
      email: identity.email,
      roles: roleChips(identity.roles),
    },
    // Immer zeigen (auch mit einem Konto), sonst fehlt der Einstieg in den Kontowechsel (K7).
    accounts,
    entries: buildAccountEntries(
      {
        platform,
        adminCapable,
        openShortcuts: () => setShortcutsOpen(true),
        openHelp: openPalette,
      },
      contributed,
    ),
    onSwitchAccount: (id) => {
      const target = accounts.find((a) => a.id === id);
      void session
        .switchAccount(id)
        .catch(() => toast.error(`Couldn’t switch to ${target?.name ?? 'that account'}.`));
    },
    onAddAccount:
      identity.authMode === 'supabase' ? undefined : () => window.location.assign('/login?add=1'),
    onSignOut: () => void session.signOut(false),
    onSignOutAll: () => void session.signOut(true),
  };

  const onNavigate = (href: string, { reload }: { reload?: boolean }) => {
    if (reload) window.location.assign(href);
    else void navigate(href);
  };

  const overlays = (
    <>
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        commands={commands}
        providers={providers}
        onNavigate={onNavigate}
        onAskAi={(q) => window.location.assign(`/ai/new?q=${encodeURIComponent(q)}`)}
        recentKey={`bar.palette.recent.${platform}`}
      />
      <ShortcutsDialog platform={platform} open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <Toaster />
    </>
  );
  return { account, openPalette, overlays };
}
